// WHY — sortable / filterable table for the dashboard. Hovering a row notifies
// the parent for chart highlighting; clicking the row body pins the highlight;
// the account-name link still navigates (uses stopPropagation so the click
// doesn't pin then immediately navigate).

import { useState, useMemo } from "react";
import type { AccountStatus } from "../lib/types";
import { fmtMoney, fmtInt } from "../lib/api";
import { Sparkline } from "./Sparkline";
import { StatusDot } from "./StatusDot";

export interface TableAccount {
  account_id: string;
  display_name: string;
  currency: string;
  status: AccountStatus;
  spend_7d: number;
  leads_7d: number;
  cpl_7d: number | null;
  cpl_delta_pct: number | null;
  sparkline: Array<{ date: string; cpl: number | null }>;
}

interface Props {
  accounts: TableAccount[];
  highlightedIds: Set<string>;
  onToggleHighlight: (id: string) => void;
  onHover: (id: string | null) => void;
}

type SortKey = "status" | "spend" | "cpl-change" | "leads";
type FilterKey = "flagged" | "all";

const STATUS_RANK: Record<AccountStatus, number> = {
  flagged: 0,
  warning: 1,
  healthy: 2,
  idle: 3,
};

function fmtDeltaPct(d: number | null): { text: string; tone: "up" | "down" | "flat" } {
  if (d === null || Math.abs(d) < 0.02) return { text: "—", tone: "flat" };
  if (d > 0) return { text: `▲ ${Math.abs(d * 100).toFixed(1)}%`, tone: "up" };
  return { text: `▼ ${Math.abs(d * 100).toFixed(1)}%`, tone: "down" };
}

const TONE_COLORS: Record<"up" | "down" | "flat", string> = {
  up: "#d9645c",
  down: "#6dbf78",
  flat: "var(--color-bone-dim)",
};

export function AccountTable({ accounts, highlightedIds, onToggleHighlight, onHover }: Props) {
  const [sort, setSort] = useState<SortKey>("status");
  const [filter, setFilter] = useState<FilterKey>("all");

  const flaggedCount = useMemo(
    () => accounts.filter((a) => a.status === "flagged" || a.status === "warning").length,
    [accounts]
  );

  const filtered = useMemo(() => {
    if (filter === "flagged") {
      return accounts.filter((a) => a.status === "flagged" || a.status === "warning");
    }
    return accounts;
  }, [accounts, filter]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    switch (sort) {
      case "status":
        copy.sort((a, b) => {
          const rankDiff = STATUS_RANK[a.status] - STATUS_RANK[b.status];
          if (rankDiff !== 0) return rankDiff;
          return b.spend_7d - a.spend_7d;
        });
        break;
      case "spend":
        copy.sort((a, b) => b.spend_7d - a.spend_7d);
        break;
      case "leads":
        copy.sort((a, b) => b.leads_7d - a.leads_7d);
        break;
      case "cpl-change":
        copy.sort((a, b) => Math.abs(b.cpl_delta_pct ?? 0) - Math.abs(a.cpl_delta_pct ?? 0));
        break;
    }
    return copy;
  }, [filtered, sort]);

  const pillBase: React.CSSProperties = {
    padding: "4px 10px",
    borderRadius: 12,
    fontFamily: "var(--font-mono)",
    textTransform: "uppercase",
    letterSpacing: "var(--letter-label)",
    fontSize: "var(--fs-xs)",
    cursor: "pointer",
    border: "none",
    transition: "background 0.15s, color 0.15s",
  };

  const pillFlaggedActive: React.CSSProperties = {
    ...pillBase,
    background: "rgba(217,100,92,0.2)",
    color: "#ffc8c4",
  };
  const pillAllActive: React.CSSProperties = {
    ...pillBase,
    background: "rgba(255,255,255,0.15)",
    color: "var(--color-bone)",
  };
  const pillInactive: React.CSSProperties = {
    ...pillBase,
    background: "rgba(255,255,255,0.05)",
    color: "var(--color-bone-dim)",
  };

  const GRID = "1fr 120px 80px 80px 100px 80px";

  return (
    <div>
      {/* Controls row */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "var(--space-2)",
          marginBottom: "var(--space-3)",
        }}
      >
        {/* Filter pills */}
        <div style={{ display: "flex", gap: "var(--space-2)" }}>
          <button
            style={filter === "flagged" ? pillFlaggedActive : pillInactive}
            aria-pressed={filter === "flagged"}
            onClick={() => setFilter("flagged")}
          >
            Flagged · {flaggedCount}
          </button>
          <button
            style={filter === "all" ? pillAllActive : pillInactive}
            aria-pressed={filter === "all"}
            onClick={() => setFilter("all")}
          >
            All · {accounts.length}
          </button>
        </div>

        {/* Sort dropdown */}
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as SortKey)}
          style={{
            background: "rgba(255,255,255,0.05)",
            border: "none",
            padding: "4px 8px",
            borderRadius: 4,
            color: "var(--color-bone)",
            fontSize: "var(--fs-sm)",
            cursor: "pointer",
          }}
        >
          <option value="status">Sort: Status</option>
          <option value="spend">Sort: Spend</option>
          <option value="cpl-change">Sort: CPL Change</option>
          <option value="leads">Sort: Leads</option>
        </select>
      </div>

      {/* Column headers */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: GRID,
          padding: "0 var(--space-2)",
          marginBottom: "var(--space-2)",
          fontSize: "var(--fs-xs)",
          fontFamily: "var(--font-mono)",
          textTransform: "uppercase",
          letterSpacing: "var(--letter-label)",
          color: "var(--color-bone-dim)",
        }}
      >
        <span>Account</span>
        <span>Trend</span>
        <span style={{ textAlign: "right" }}>CPL 7d</span>
        <span style={{ textAlign: "right" }}>Leads</span>
        <span style={{ textAlign: "right" }}>Spend</span>
        <span style={{ textAlign: "right" }}>Δ CPL</span>
      </div>

      {/* Table body */}
      <div role="table">
        {sorted.map((a) => {
          const pinned = highlightedIds.has(a.account_id);
          const delta = fmtDeltaPct(a.cpl_delta_pct);

          return (
            <div
              key={a.account_id}
              role="row"
              className="account-table-row"
              onClick={() => onToggleHighlight(a.account_id)}
              onMouseEnter={() => onHover(a.account_id)}
              onMouseLeave={() => onHover(null)}
              style={{
                display: "grid",
                gridTemplateColumns: GRID,
                alignItems: "center",
                minHeight: 44,
                padding: "0 var(--space-2)",
                borderBottom: "1px solid rgba(255,255,255,0.06)",
                background: pinned ? "rgba(255,255,255,0.03)" : "transparent",
                cursor: "pointer",
                transition: "background 0.1s",
              }}
            >
              {/* Name cell */}
              <div style={{ display: "flex", alignItems: "center", gap: 0, overflow: "hidden" }}>
                <StatusDot status={a.status} />
                <a
                  href={`/dashboard/account/${encodeURIComponent(a.account_id)}`}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    color: "inherit",
                    textDecoration: "none",
                    fontSize: "var(--fs-sm)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {a.display_name}
                </a>
              </div>

              {/* Sparkline */}
              <div style={{ display: "flex", alignItems: "center" }}>
                <Sparkline
                  points={a.sparkline.map((p) => p.cpl)}
                  ariaLabel={`${a.display_name} CPL trend`}
                />
              </div>

              {/* CPL 7d */}
              <div
                style={{
                  textAlign: "right",
                  fontSize: "var(--fs-sm)",
                  fontFamily: "var(--font-mono)",
                }}
              >
                {a.cpl_7d !== null ? fmtMoney(a.cpl_7d, a.currency) : "—"}
              </div>

              {/* Leads */}
              <div
                style={{
                  textAlign: "right",
                  fontSize: "var(--fs-sm)",
                  fontFamily: "var(--font-mono)",
                }}
              >
                {fmtInt(a.leads_7d)}
              </div>

              {/* Spend */}
              <div
                style={{
                  textAlign: "right",
                  fontSize: "var(--fs-sm)",
                  fontFamily: "var(--font-mono)",
                }}
              >
                {a.spend_7d > 0 ? fmtMoney(a.spend_7d, a.currency) : "—"}
              </div>

              {/* CPL delta */}
              <div
                style={{
                  textAlign: "right",
                  fontSize: "var(--fs-sm)",
                  fontFamily: "var(--font-mono)",
                  color: TONE_COLORS[delta.tone],
                }}
              >
                {delta.text}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
